import type { EngineContext } from "../../context";
import { XMLBuilder } from "fast-xml-parser";
import { buildSplitRecipientDetail } from "../../constants/recipientIdentity";
import { resolveIrsIntakeTaxYear, schemaVersionForTaxYear, softwareIdForTaxYear } from "../../constants/taxYear";
import { Form1099DIV } from "./interface/1099-DIV-interface";
import {
  generateRandomRecordId,
  generateUniqueSubmissionId,
  generateUniqueTransmissionId,
} from "../../constants/genRecordId";
import { parseAmount } from "../../constants/validationFunctions";
import {
  buildTransmitterGroup,
  buildVendorGroup,
  buildIssuerDetail,
} from "../../xml/transmitterData";
import { getValidCountryCode } from "../../constants/formatDate";

// Define TypeScript interfaces for the structure
interface IssuerDetailType {
  ForeignEntityInd: string;
  TIN: string;
  TINSubmittedTypeCd: string;
  PersonNameControlTxt: string;
  PersonName?: {
    PersonFirstNm: string;
    PersonLastNm: string; // Required by IRS schema
  };
  MailingAddressGrp?: {
    USAddress: {
      AddressLine1Txt?: string;
      AddressLine2Txt?: string;
      CityNm?: string;
      StateAbbreviationCd?: string;
      ZIPCd?: string;
    };
  };
  PhoneNum?: string;
}

interface TotalsType {
  FederalIncomeTaxWithheldAmt: string;
  TotalOrdinaryDividendsAmt: string;
  QualifiedDividendsAmt: string;
  TotalCapitalDistributionsAmt: string;
  UnrecapturedSection1250GainAmt: string;
  CapitalGainSect1202Amt: string;
  Collectibles28PercentGainAmt: string;
  Section897OrdinaryDividendsAmt: string;
  Section897CapitalGainAmt: string;
  NondividendDistributionsAmt: string;
  Section199ADividendsAmt: string;
  InvestmentExpenseAmt: string;
  ForeignTaxesPaidAmt: string;
  CashLiquidationDistriAmt: string;
  NoncashLiquidationDistriAmt: string;
  ExemptInterestDividendsAmt: string;
  PrivateActivityBondIntDivAmt: string;
}

function formatIrsAmount(amount: string | number): string {
  // If amount is a string, convert to number first
  const numericAmount =
    typeof amount === "string" ? parseFloat(amount) : amount;

  // Convert to integer by removing decimal places (round to whole dollar)
  return Math.round(numericAmount).toString();
}

export async function generate1099DIVXml(
  ctx: EngineContext,records: Form1099DIV[]) {
  if (!records || !Array.isArray(records) || records.length === 0) {
    throw new Error("No records found or records input is invalid.");
  }

  // Fetch the transmitter data
  const transmitterData = ctx.transmitter as any;

  const builder = new XMLBuilder({
    ignoreAttributes: false,
    format: true,
    suppressEmptyNode: true,
    attributeNamePrefix: "@_",
    processEntities: true,
  });

  // Determine tax year from the records or use 2025 as default (2025 schema)
  const taxYear = resolveIrsIntakeTaxYear(records[0], ctx.irs.maxIntakeTaxYear);
  // Step 1: Group records by FILER_FEDERAL_ID_NUMBER
  const recordsByFiler: Record<string, Form1099DIV[]> = {};

  records.forEach((record) => {
    if (!record.FILER_FEDERAL_ID_NUMBER) {
      console.warn("Record missing FILER_FEDERAL_ID_NUMBER, using default");
      record.FILER_FEDERAL_ID_NUMBER = "000000000";
    }

    const filerId = record.FILER_FEDERAL_ID_NUMBER;
    if (!recordsByFiler[filerId]) {
      recordsByFiler[filerId] = [];
    }
    recordsByFiler[filerId].push(record);
  });

  // Generate unique transmission ID
  const uniqueTransmissionId = generateUniqueTransmissionId(ctx.irs.tcc);

  // Step 2: Create a submission group for each filer
  const irSubmissionGroups = Object.keys(recordsByFiler).map((filerId) => {
    const filerRecords = recordsByFiler[filerId];
    const firstRecord = filerRecords[0]; // Use first record for filer info

    // Create submission ID for this filer
    const submissionId = generateUniqueSubmissionId();

    // Step 3: Build the IssuerDetail directly from the record's filer information
    const issuerDetail: IssuerDetailType = {
      ForeignEntityInd: "0",
      TIN: ctx.irs.testIndicator
        ? "000000000"
        : (firstRecord.FILER_FEDERAL_ID_NUMBER || "000000000").replace(
            /-/g,
            ""
          ),
      TINSubmittedTypeCd: "INDIVIDUAL_TIN",
      PersonNameControlTxt: firstRecord.FILER_NAME
        ? firstRecord.FILER_NAME.trim().substring(0, 4).toUpperCase()
        : "PAYS",
    };

    // Add PersonName if available - MUST include both first and last name
    if (firstRecord.FILER_NAME) {
      issuerDetail.PersonName = {
        PersonFirstNm: firstRecord.FILER_NAME,
        PersonLastNm: firstRecord.FILER_NAME || "COMPANY", // Fallback to COMPANY if no last name
      };
    }

    // Add MailingAddressGrp if address info is available
    if (
      firstRecord.FILER_ADDRESS1 ||
      firstRecord.FILER_CITY ||
      firstRecord.FILER_STATE ||
      firstRecord.FILER_ZIP
    ) {
      issuerDetail.MailingAddressGrp = {
        USAddress: {},
      };

      if (firstRecord.FILER_ADDRESS1) {
        issuerDetail.MailingAddressGrp.USAddress.AddressLine1Txt =
          firstRecord.FILER_ADDRESS1;
      }

      if (firstRecord.FILER_ADDRESS2) {
        issuerDetail.MailingAddressGrp.USAddress.AddressLine2Txt =
          firstRecord.FILER_ADDRESS2;
      }

      if (firstRecord.FILER_CITY) {
        issuerDetail.MailingAddressGrp.USAddress.CityNm =
          firstRecord.FILER_CITY;
      }

      if (firstRecord.FILER_STATE) {
        issuerDetail.MailingAddressGrp.USAddress.StateAbbreviationCd =
          firstRecord.FILER_STATE;
      }

      if (firstRecord.FILER_ZIP) {
        issuerDetail.MailingAddressGrp.USAddress.ZIPCd = firstRecord.FILER_ZIP;
      }
    }

    // Add phone if available
    if (firstRecord.FILER_PHONE) {
      issuerDetail.PhoneNum = firstRecord.FILER_PHONE.replace(/[^\d]/g, "");
    }

    // Step 4: Process recipient records for this filer
    const form1099DIVDetails = filerRecords.map((record) => {
      // Validate required fields are present
      if (!record.RECIPIENT_ID_NUMBER) {
        console.warn("Record missing RECIPIENT_ID_NUMBER");
      }

      if (!record.RECIPIENT_NAME) {
        console.warn("Record missing RECIPIENT_NAME");
      }

      // Validate and force a valid state code for US addresses
      const validState = record.RECIPIENT_STATE?.substring(0, 2) || "XX";

      return {
        "@_xmlns": "urn:us:gov:treasury:irs:ir",
        "@_xmlns:xsd": "http://www.w3.org/2001/XMLSchema",
        TaxYr: taxYear,
        RecordId: record.id.toString(),
        IssuerOfficeCd: "MAIN",
        VoidInd: "0",
        CorrectedInd: record.corrected ? "1" : "0",
        // TY2026 layout-V2 records file via the shared split builder (business
        // as BusinessName+BUSINESS_TIN); legacy records keep the shape below.
        RecipientDetail: buildSplitRecipientDetail(
          record,
          ctx.irs.testIndicator
            ? "000000000"
            : (record.RECIPIENT_ID_NUMBER || "000000000").replace(/-/g, ""),
          record.FOREIGN_COUNTRY_INDICATOR === "X" && record.FOREIGN_COUNTRY
            ? {
                ForeignAddress: {
                  AddressLine1Txt:
                    record.RECIPIENT_ADDRESS1 || "UNKNOWN ADDRESS",
                  ...(record.RECIPIENT_ADDRESS2 && {
                    AddressLine2Txt: record.RECIPIENT_ADDRESS2,
                  }),
                  CityNm: record.RECIPIENT_CITY || "UNKNOWN CITY",
                  CountryCd: getValidCountryCode(record.FOREIGN_COUNTRY),
                  ...(record.RECIPIENT_ZIP && {
                    ForeignPostalCd: record.RECIPIENT_ZIP,
                  }),
                },
              }
            : {
                USAddress: {
                  AddressLine1Txt:
                    record.RECIPIENT_ADDRESS1 || "UNKNOWN ADDRESS",
                  ...(record.RECIPIENT_ADDRESS2 && {
                    AddressLine2Txt: record.RECIPIENT_ADDRESS2,
                  }),
                  CityNm: record.RECIPIENT_CITY || "UNKNOWN CITY",
                  StateAbbreviationCd: validState,
                  ZIPCd: record.RECIPIENT_ZIP || "00000",
                },
              }
        ) ?? {
          TIN: ctx.irs.testIndicator
            ? "000000000"
            : (record.RECIPIENT_ID_NUMBER || "000000000").replace(/-/g, ""),
          TINSubmittedTypeCd: "INDIVIDUAL_TIN",
          PersonNameControlTxt: (record.RECIPIENT_NAME || "UNKN")
            .trim()
            .substring(0, 4)
            .toUpperCase(),
          PersonName: {
            PersonFirstNm: record.RECIPIENT_NAME || "Unknown",
            PersonLastNm: record.RECIPIENT_NAME2 || "RECIPIENT", // Ensure LastNm is always populated
          },
          MailingAddressGrp:
            record.FOREIGN_COUNTRY_INDICATOR === "X" && record.FOREIGN_COUNTRY
              ? {
                  // For foreign addresses
                  ForeignAddress: {
                    AddressLine1Txt:
                      record.RECIPIENT_ADDRESS1 || "UNKNOWN ADDRESS",
                    ...(record.RECIPIENT_ADDRESS2 && {
                      AddressLine2Txt: record.RECIPIENT_ADDRESS2,
                    }),
                    CityNm: record.RECIPIENT_CITY || "UNKNOWN CITY",
                    CountryCd: getValidCountryCode(record.FOREIGN_COUNTRY),
                    ...(record.RECIPIENT_ZIP && {
                      ForeignPostalCd: record.RECIPIENT_ZIP,
                    }),
                  },
                }
              : {
                  // For US addresses
                  USAddress: {
                    AddressLine1Txt:
                      record.RECIPIENT_ADDRESS1 || "UNKNOWN ADDRESS",
                    ...(record.RECIPIENT_ADDRESS2 && {
                      AddressLine2Txt: record.RECIPIENT_ADDRESS2,
                    }),
                    CityNm: record.RECIPIENT_CITY || "UNKNOWN CITY",
                    StateAbbreviationCd: validState,
                    ZIPCd: record.RECIPIENT_ZIP || "00000",
                  },
                },
        },
        ...(record.ACCOUNT_NUMBER && {
          RecipientAccountNum: record.ACCOUNT_NUMBER,
        }),
        FATCAFilingRequirementInd:
          record.BOX11_FATCA_REQUIREMENT === "X" ? "1" : "0",
        SecondTINNoticeInd: record.BOX_2ND_TIN_NOT_IND === "X" ? "1" : "0",
        ...(record.BOX1A_TOTAL_ORDINARY_DIVIDENDS && {
          TotalOrdinaryDividendsAmt: formatIrsAmount(
            parseAmount(record.BOX1A_TOTAL_ORDINARY_DIVIDENDS)
          ),
        }),
        ...(record.BOX1B_QUALIFIED_DIVIDENDS && {
          QualifiedDividendsAmt: formatIrsAmount(
            parseAmount(record.BOX1B_QUALIFIED_DIVIDENDS)
          ),
        }),
        ...(record.BOX2A_TOTAL_CAPITAL_GAIN_DISTR && {
          TotalCapitalDistributionsAmt: formatIrsAmount(
            parseAmount(record.BOX2A_TOTAL_CAPITAL_GAIN_DISTR)
          ),
        }),
        ...(record.BOX2B_UNRECAP_SEC_1250_GAIN && {
          UnrecapturedSection1250GainAmt: formatIrsAmount(
            parseAmount(record.BOX2B_UNRECAP_SEC_1250_GAIN)
          ),
        }),
        ...(record.BOX2C_SECTION_1202_GAIN && {
          CapitalGainSect1202Amt: formatIrsAmount(
            parseAmount(record.BOX2C_SECTION_1202_GAIN)
          ),
        }),
        ...(record.BOX2D_COLLECTIBLES_GAIN && {
          Collectibles28PercentGainAmt: formatIrsAmount(
            parseAmount(record.BOX2D_COLLECTIBLES_GAIN)
          ),
        }),
        ...(record.BOX2E_SECTION_897_ORD_DIV && {
          Section897OrdinaryDividendsAmt: formatIrsAmount(
            parseAmount(record.BOX2E_SECTION_897_ORD_DIV)
          ),
        }),
        ...(record.BOX2F_SECTION_897_CAP_GAIN && {
          Section897CapitalGainAmt: formatIrsAmount(
            parseAmount(record.BOX2F_SECTION_897_CAP_GAIN)
          ),
        }),
        ...(record.BOX3_NONDIVIDEND_DISTRIBUTIONS && {
          NondividendDistributionsAmt: formatIrsAmount(
            parseAmount(record.BOX3_NONDIVIDEND_DISTRIBUTIONS)
          ),
        }),
        ...(record.BOX4_FEDERAL_INCOME_TAX_WITHHELD && {
          FederalIncomeTaxWithheldAmt: formatIrsAmount(
            parseAmount(record.BOX4_FEDERAL_INCOME_TAX_WITHHELD)
          ),
        }),
        ...(record.BOX5_SECTION_199A_DIVIDENDS && {
          Section199ADividendsAmt: formatIrsAmount(
            parseAmount(record.BOX5_SECTION_199A_DIVIDENDS)
          ),
        }),
        ...(record.BOX6_INVESTMENT_EXPENSES && {
          InvestmentExpenseAmt: formatIrsAmount(
            parseAmount(record.BOX6_INVESTMENT_EXPENSES)
          ),
        }),
        ...(record.BOX7_FOREIGN_TAX_PAID && {
          ForeignTaxesPaidAmt: formatIrsAmount(
            parseAmount(record.BOX7_FOREIGN_TAX_PAID)
          ),
        }),
        ...(record.BOX8_FOREIGN_COUNTRY && {
          ForeignCountryOrUSPossessionCd: getValidCountryCode(
            record.BOX8_FOREIGN_COUNTRY
          ),
        }),
        ...(record.BOX9_CASH_LIQUIDATION_DISTRIBUTIONS && {
          CashLiquidationDistriAmt: formatIrsAmount(
            parseAmount(record.BOX9_CASH_LIQUIDATION_DISTRIBUTIONS)
          ),
        }),
        ...(record.BOX10_NONCASH_LIQUIDATION_DISTRIBUTIONS && {
          NoncashLiquidationDistriAmt: formatIrsAmount(
            parseAmount(record.BOX10_NONCASH_LIQUIDATION_DISTRIBUTIONS)
          ),
        }),
        ...(record.BOX12_EXEMPT_INTEREST_DIVIDENDS && {
          ExemptInterestDividendsAmt: formatIrsAmount(
            parseAmount(record.BOX12_EXEMPT_INTEREST_DIVIDENDS)
          ),
        }),
        ...(record.BOX13_SPECIFIED_PRIVATE_ACTIVITY_BOND && {
          PrivateActivityBondIntDivAmt: formatIrsAmount(
            parseAmount(record.BOX13_SPECIFIED_PRIVATE_ACTIVITY_BOND)
          ),
        }),
        ...(shouldIncludeStateLocalTaxGrp(record) && {
          StateLocalTaxGrp: getStateLocalTaxGrp(record),
        }),
      };
    });

    // Calculate totals for this filer's records
    const totals = calculateTotalsForFiler(filerRecords);

    // Step 5: Return a submission group for this filer
    return {
      IRSubmission1Header: {
        SubmissionId: submissionId,
        TaxYr: taxYear,
        IssuerDetail: issuerDetail,
        ContactPersonInformationGrp: {
          ContactPersonNm: firstRecord.FILER_NAME || "Contact Person",
          ContactPhoneNum:
            firstRecord.FILER_PHONE?.replace(/[^\d]/g, "") || "0000000000",
          ContactEmailAddressTxt: "support@ts1099.com", // Use a default or add to your form1099DIV_2024 table
        },
        FormTypeCd: "1099DIV",
        ParentFormTypeCd: "1096",
        CFSFElectionInd: "0",
        JuratSignatureGrp: {
          SignatureIntentInd: "1",
          JuratSignaturePIN: "00000",
          SignatureDt: new Date().toISOString().split("T")[0], // Use current date in YYYY-MM-DD format
          JuratPersonTitleTxt: "Payer",
          PersonNm: firstRecord.FILER_NAME || "Payer",
        },
        TotalReportedRcpntFormCnt: form1099DIVDetails.length.toString(),
        IRSubmission1FormTotals: {
          Form1099DIVTotalAmtGrp: totals,
        },
      },
      IRSubmission1Detail: {
        Form1099DIVDetail: form1099DIVDetails,
      },
    };
  });

  const xmlObj = {
    "?xml": {
      "@_version": "1.0",
      "@_encoding": "UTF-8",
    },
    IRTransmission: {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
      "@_xsi:schemaLocation":
        "urn:us:gov:treasury:irs:ir IRS-IRIntakeTransmissionMessage.xsd",
      IRTransmissionManifest: {
        SchemaVersionNum: schemaVersionForTaxYear(taxYear),
        UniqueTransmissionId: uniqueTransmissionId,
        TaxYr: taxYear,
        PriorYearDataInd: "0",
        TransmissionTypeCd: "O",
        TestCd: ctx.irs.testIndicator ? "T" : "P",
        TransmitterGrp: buildTransmitterGroup(transmitterData),
        VendorCd: "V",
        SoftwareId: softwareIdForTaxYear(taxYear, ctx.irs),
        VendorGrp: buildVendorGroup(transmitterData),
        TotalIssuerFormCnt: Object.keys(recordsByFiler).length.toString(),
        TotalRecipientFormCnt: records.length.toString(),
        PaperSubmissionInd: "0",
        MediaSourceCd: "M",
        SubmissionChannelCd: "A2A",
      },
      IRSubmission1Grp: irSubmissionGroups,
    },
  };

  try {
    const xml = builder.build(xmlObj);
    return { xml, taxYear };
  } catch (error: any) {
    console.error("Error generating XML:", error);
    throw new Error(`Failed to generate XML: ${error.message}`);
  }
}

// Helper function to calculate totals for a specific filer's records
function calculateTotalsForFiler(records: Form1099DIV[]): TotalsType {
  // Initialize totals object - order matches 2025 schema
  const totals: TotalsType = {
    FederalIncomeTaxWithheldAmt: "0",
    TotalOrdinaryDividendsAmt: "0",
    QualifiedDividendsAmt: "0",
    TotalCapitalDistributionsAmt: "0",
    UnrecapturedSection1250GainAmt: "0",
    CapitalGainSect1202Amt: "0",
    Collectibles28PercentGainAmt: "0",
    Section897OrdinaryDividendsAmt: "0",
    Section897CapitalGainAmt: "0",
    NondividendDistributionsAmt: "0",
    Section199ADividendsAmt: "0",
    InvestmentExpenseAmt: "0",
    ForeignTaxesPaidAmt: "0",
    CashLiquidationDistriAmt: "0",
    NoncashLiquidationDistriAmt: "0",
    ExemptInterestDividendsAmt: "0",
    PrivateActivityBondIntDivAmt: "0",
  };

  // Calculate actual totals from records - use integer values
  records.forEach((record) => {
    if (record.BOX1A_TOTAL_ORDINARY_DIVIDENDS) {
      const amount = parseFloat(
        parseAmount(record.BOX1A_TOTAL_ORDINARY_DIVIDENDS)
      );
      totals.TotalOrdinaryDividendsAmt = formatIrsAmount(
        parseFloat(totals.TotalOrdinaryDividendsAmt) + amount
      );
    }

    if (record.BOX1B_QUALIFIED_DIVIDENDS) {
      const amount = parseFloat(parseAmount(record.BOX1B_QUALIFIED_DIVIDENDS));
      totals.QualifiedDividendsAmt = formatIrsAmount(
        parseFloat(totals.QualifiedDividendsAmt) + amount
      );
    }

    if (record.BOX2A_TOTAL_CAPITAL_GAIN_DISTR) {
      const amount = parseFloat(
        parseAmount(record.BOX2A_TOTAL_CAPITAL_GAIN_DISTR)
      );
      totals.TotalCapitalDistributionsAmt = formatIrsAmount(
        parseFloat(totals.TotalCapitalDistributionsAmt) + amount
      );
    }

    if (record.BOX2B_UNRECAP_SEC_1250_GAIN) {
      const amount = parseFloat(
        parseAmount(record.BOX2B_UNRECAP_SEC_1250_GAIN)
      );
      totals.UnrecapturedSection1250GainAmt = formatIrsAmount(
        parseFloat(totals.UnrecapturedSection1250GainAmt) + amount
      );
    }

    if (record.BOX2C_SECTION_1202_GAIN) {
      const amount = parseFloat(parseAmount(record.BOX2C_SECTION_1202_GAIN));
      totals.CapitalGainSect1202Amt = formatIrsAmount(
        parseFloat(totals.CapitalGainSect1202Amt) + amount
      );
    }

    if (record.BOX2D_COLLECTIBLES_GAIN) {
      const amount = parseFloat(parseAmount(record.BOX2D_COLLECTIBLES_GAIN));
      totals.Collectibles28PercentGainAmt = formatIrsAmount(
        parseFloat(totals.Collectibles28PercentGainAmt) + amount
      );
    }

    if (record.BOX2E_SECTION_897_ORD_DIV) {
      const amount = parseFloat(parseAmount(record.BOX2E_SECTION_897_ORD_DIV));
      totals.Section897OrdinaryDividendsAmt = formatIrsAmount(
        parseFloat(totals.Section897OrdinaryDividendsAmt) + amount
      );
    }

    if (record.BOX2F_SECTION_897_CAP_GAIN) {
      const amount = parseFloat(parseAmount(record.BOX2F_SECTION_897_CAP_GAIN));
      totals.Section897CapitalGainAmt = formatIrsAmount(
        parseFloat(totals.Section897CapitalGainAmt) + amount
      );
    }

    if (record.BOX3_NONDIVIDEND_DISTRIBUTIONS) {
      const amount = parseFloat(
        parseAmount(record.BOX3_NONDIVIDEND_DISTRIBUTIONS)
      );
      totals.NondividendDistributionsAmt = formatIrsAmount(
        parseFloat(totals.NondividendDistributionsAmt) + amount
      );
    }

    if (record.BOX4_FEDERAL_INCOME_TAX_WITHHELD) {
      const amount = parseFloat(
        parseAmount(record.BOX4_FEDERAL_INCOME_TAX_WITHHELD)
      );
      totals.FederalIncomeTaxWithheldAmt = formatIrsAmount(
        parseFloat(totals.FederalIncomeTaxWithheldAmt) + amount
      );
    }

    if (record.BOX5_SECTION_199A_DIVIDENDS) {
      const amount = parseFloat(
        parseAmount(record.BOX5_SECTION_199A_DIVIDENDS)
      );
      totals.Section199ADividendsAmt = formatIrsAmount(
        parseFloat(totals.Section199ADividendsAmt) + amount
      );
    }

    if (record.BOX6_INVESTMENT_EXPENSES) {
      const amount = parseFloat(parseAmount(record.BOX6_INVESTMENT_EXPENSES));
      totals.InvestmentExpenseAmt = formatIrsAmount(
        parseFloat(totals.InvestmentExpenseAmt) + amount
      );
    }

    if (record.BOX7_FOREIGN_TAX_PAID) {
      const amount = parseFloat(parseAmount(record.BOX7_FOREIGN_TAX_PAID));
      totals.ForeignTaxesPaidAmt = formatIrsAmount(
        parseFloat(totals.ForeignTaxesPaidAmt) + amount
      );
    }

    if (record.BOX9_CASH_LIQUIDATION_DISTRIBUTIONS) {
      const amount = parseFloat(
        parseAmount(record.BOX9_CASH_LIQUIDATION_DISTRIBUTIONS)
      );
      totals.CashLiquidationDistriAmt = formatIrsAmount(
        parseFloat(totals.CashLiquidationDistriAmt) + amount
      );
    }

    if (record.BOX10_NONCASH_LIQUIDATION_DISTRIBUTIONS) {
      const amount = parseFloat(
        parseAmount(record.BOX10_NONCASH_LIQUIDATION_DISTRIBUTIONS)
      );
      totals.NoncashLiquidationDistriAmt = formatIrsAmount(
        parseFloat(totals.NoncashLiquidationDistriAmt) + amount
      );
    }

    if (record.BOX12_EXEMPT_INTEREST_DIVIDENDS) {
      const amount = parseFloat(
        parseAmount(record.BOX12_EXEMPT_INTEREST_DIVIDENDS)
      );
      totals.ExemptInterestDividendsAmt = formatIrsAmount(
        parseFloat(totals.ExemptInterestDividendsAmt) + amount
      );
    }

    if (record.BOX13_SPECIFIED_PRIVATE_ACTIVITY_BOND) {
      const amount = parseFloat(
        parseAmount(record.BOX13_SPECIFIED_PRIVATE_ACTIVITY_BOND)
      );
      totals.PrivateActivityBondIntDivAmt = formatIrsAmount(
        parseFloat(totals.PrivateActivityBondIntDivAmt) + amount
      );
    }
  });

  return totals;
}

/**
 * Helper function to determine if state/local tax groups should be included.
 */
function shouldIncludeStateLocalTaxGrp(record: Form1099DIV): boolean {
  return !!(
    (record.BOX15_STATE_IDENTIFICATION_NO1 &&
      (parseFloat(
        parseAmount(String(record.BOX16_STATE_TAX_WITHHELD1 || "0"))
      ) > 0 ||
        parseFloat(parseAmount(String(record.BOX14_STATE1 || "0"))) > 0)) ||
    (record.BOX15_STATE_IDENTIFICATION_NO2 &&
      (parseFloat(
        parseAmount(String(record.BOX16_STATE_TAX_WITHHELD2 || "0"))
      ) > 0 ||
        parseFloat(parseAmount(String(record.BOX14_STATE2 || "0"))) > 0))
  );
}

/**
 * Helper function to build state/local tax group arrays.
 */
function getStateLocalTaxGrp(record: Form1099DIV): any[] {
  const groups = [];

  if (
    record.BOX15_STATE_IDENTIFICATION_NO1 &&
    (parseFloat(parseAmount(String(record.BOX16_STATE_TAX_WITHHELD1 || "0"))) >
      0 ||
      parseFloat(parseAmount(String(record.BOX14_STATE1 || "0"))) > 0)
  ) {
    groups.push({
      StateAbbreviationCd: record.RECIPIENT_STATE.substring(0, 2),
      StateTaxGrp: {
        StateTaxWithheldAmt: parseAmount(record.BOX16_STATE_TAX_WITHHELD1),
        StateIncomeAmt: parseAmount(record.BOX14_STATE1),
      },
    });
  }

  if (
    record.BOX15_STATE_IDENTIFICATION_NO2 &&
    (parseFloat(parseAmount(String(record.BOX16_STATE_TAX_WITHHELD2 || "0"))) >
      0 ||
      parseFloat(parseAmount(String(record.BOX14_STATE2 || "0"))) > 0)
  ) {
    groups.push({
      StateAbbreviationCd: record.RECIPIENT_STATE.substring(0, 2),
      StateTaxGrp: {
        StateTaxWithheldAmt: formatIrsAmount(
          parseAmount(String(record.BOX16_STATE_TAX_WITHHELD2 || "0"))
        ),
        ...(record.BOX14_STATE2 && {
          StateIncomeAmt: formatIrsAmount(
            parseAmount(String(record.BOX14_STATE2))
          ),
        }),
      },
    });
  }

  return groups;
}
